import { useCallback, useMemo, useState } from 'react';
import PropTypes from "prop-types";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Popover,
  IconButton,
  Tooltip,
  Badge,
  Typography,
  LinearProgress,
} from "@mui/material";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import NotificationsList from "./NotificationsList";
import Pagination from "../../../components/Pagination";
import { makeStyles } from 'tss-react/mui';

// CREW-1188 — a popover anchored to the bell. It used to be a Dialog meant to
// sit top-right at 30vw through `PaperProps`; MUI 9 no longer reads
// `PaperProps` on a Dialog, so it came up as a small box mid-screen instead.
const useStyles = makeStyles()((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0.5, 0.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  body: {
    flex: 1,
    overflowY: "auto",
  },
  pagination: {
    position: "sticky",
    bottom: 0,
    zIndex: 3,
    background: "rgba(255, 255, 255, .85)",
    marginTop: 0,
    paddingBottom: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
  },
}));

const anchorOrigin = { vertical: "bottom", horizontal: "right" };
const transformOrigin = { vertical: "top", horizontal: "right" };
const paperSlotProps = {
  paper: {
    sx: {
      width: 420,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "min(70vh, 640px)",
      display: "flex",
      flexDirection: "column",
      mt: 0.5,
    },
  },
};

const NotificationManager = ({
  data,
  pagination,
  isLoading,
  onChangePageNumber,
  loadItemsRequest,
  notificationCount,
  clearAllNotifications,
  clearNotification,
}) => {
  const { classes } = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);

  const paginationOverrideClasses = useMemo(
    () => ({
      pagination: classes.pagination,
    }),
    [classes]
  );

  const handleOpen = useCallback((event) => {
    loadItemsRequest();
    setAnchorEl(event.currentTarget);
  }, [loadItemsRequest]);

  const handleClearAll = useCallback(() => {
    clearAllNotifications();
    loadItemsRequest();
  }, [clearAllNotifications, loadItemsRequest]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <Tooltip title={"Notifications"} enterDelay={300}>
        <IconButton color="primary" onClick={handleOpen} size="large" aria-label="Notifications">
          <Badge color="error" badgeContent={notificationCount}>
            <NotificationsIcon fontSize="medium" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={paperSlotProps}
      >
        <div className={classes.header}>
          <Typography variant="h6" component="h6">
            Notifications
          </Typography>
          <Tooltip title="Clear All Notifications" placement="left" enterDelay={300}>
            <IconButton color="primary" onClick={handleClearAll} size="medium">
              <ClearAllIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </div>
        {isLoading && <LinearProgress />}
        <div className={classes.body}>
          <NotificationsList
            data={data}
            clearNotification={clearNotification}
          />
          {!isLoading && data.length > 0 && (
            <Pagination
              classes={paginationOverrideClasses}
              data={pagination}
              onChange={onChangePageNumber}
            />
          )}
        </div>
      </Popover>
    </>
  );
};

NotificationManager.propTypes = {
  data: PropTypes.array,
  pagination: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  onChangePageNumber: PropTypes.func.isRequired,
  loadItemsRequest: PropTypes.func.isRequired,
  notificationCount: PropTypes.number,
  clearAllNotifications: PropTypes.func.isRequired,
  clearNotification: PropTypes.func.isRequired,
};

export default NotificationManager;
